/** Per-type evidence panel for an expanded queue finding (GIS Phase 2A,
 *  Task 11a, spec §5). Fed exactly the tagged-union `EvidenceResponse`
 *  `FindingRow` fetches lazily on expand — never for `new_in_gis` (the
 *  endpoint 422s on that type by design; `FindingRow` guards the fetch
 *  itself and never renders this panel for that type).
 *
 *  Renders the four evidence shapes `schemas/gis_queue.py::EvidenceResponse`
 *  discriminates on:
 *   - relationship_conflict/ambiguous/unresolved: the candidate-parent
 *     table (distance_m/feeder/kva, select-radio) plus the lazy `MiniMap`
 *     fed exactly `evidence.geojson` — never the main map's layers (spec
 *     §5's isolation rule). Selecting a candidate calls `onSelectCandidate`,
 *     which `FindingRow` feeds into `ActionButtons` as
 *     `selectedTargetParentId` for assign_parent/propose_parent — see that
 *     component's own docstring for the integration choice (manual input
 *     kept as fallback, pre-filled/synced from this selection).
 *   - attribute_changed: platform/GIS values + the field's
 *     `gis_field_state` shadow, when one exists + (meters-table findings
 *     only, post-2A addition, spec §5, 2026-07-28) the AMI activity hint
 *     (`evidence.ami_activity.hint`), rendered VERBATIM — the server
 *     already produces a plain-language sentence (e.g. "AMI: reporting
 *     normally (last read 2026-07-25)" or a quiet-meter physical-swap
 *     hint), so no client-side mapping is attempted. Evidence with a soft
 *     hint only, never an auto-action. `identity_key_change` (identity-key
 *     guard, task-8) reuses this exact shape and this exact case — the
 *     backend widened the tag rather than adding a new evidence model, so
 *     there is no separate branch to add here.
 *   - missing_from_gis: last-seen run/generation/alias status + the
 *     ENTITY-level retirement clock (`qualifies` + `reason`). `reason` is
 *     rendered VERBATIM — `services/gis_evidence.py::_entity_retirement_
 *     clock` already produces a plain-language sentence, not an enum, so no
 *     client-side mapping is needed or attempted.
 *   - identity_conflict: the alias owner's row + alias record, and the
 *     incoming claim off the finding itself, with the explicit "no
 *     incoming snapshot" honesty note R2-3 requires. That note is rendered
 *     UNCONDITIONALLY for this type in 2A: no code path ever populates a
 *     richer incoming snapshot yet (the immutable conflict snapshot ships
 *     with 2B's identity-transition work), so there is no "sometimes true"
 *     branch to gate it on.
 *
 *  NOTE (brief drift, documented per this task's instructions): the task
 *  brief's Step 1 also mentions a "fills/overwrites breakdown" for this
 *  panel. That field is `BulkPreviewResponse.breakdown` (bulk triage
 *  preview, Task 11b) — `EvidenceResponse` carries no such field for any of
 *  the four finding types (see `src/topology_platform/schemas/gis_queue.py`
 *  — ground truth), so this component does not render or invent one. */

import { friendlyError } from '../../utils/friendlyError.ts'
import { MiniMap } from './MiniMap.tsx'
import type {
  AttributeChangedEvidenceResponse,
  EvidenceResponse,
  IdentityConflictEvidenceResponse,
  MissingFromGisEvidenceResponse,
  RelationshipEvidenceCandidate,
  RelationshipEvidenceResponse,
} from './types.ts'

/** Meters, plain precision (spec §6 language rules): whole meters at or
 *  above 10 m ("47 m"), one decimal below ("6.4 m") — a resolved target
 *  with no measurable geometry carries `distance_m: null` (schema docs),
 *  rendered as an explicit "distance unknown" rather than a blank cell. */
function formatDistance(m: number | null): string {
  if (m === null) return 'distance unknown'
  return m >= 10 ? `${Math.round(m)} m` : `${m.toFixed(1)} m`
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/** The conductor layer is the only truncatable part of the geojson
 *  (candidates/asset are never capped) — counting `role: "conductor"`
 *  features already IN the response is what lets "showing N of M" stay
 *  accurate without duplicating the backend's cap constant client-side. */
function conductorFeatureCount(geojson: Record<string, unknown>): number {
  const features = (geojson as { features?: unknown }).features
  if (!Array.isArray(features)) return 0
  return features.filter(f => {
    const props = (f as { properties?: { role?: string } } | undefined)?.properties
    return props?.role === 'conductor'
  }).length
}

interface CandidateTableProps {
  findingId: number
  candidates: RelationshipEvidenceCandidate[]
  selectedCandidateId: number | null
  onSelectCandidate: (entityId: number) => void
}

function CandidateTable({ findingId, candidates, selectedCandidateId, onSelectCandidate }: CandidateTableProps) {
  if (candidates.length === 0) {
    return <div className="text-xs text-gray-500">No candidate parents found within range.</div>
  }
  return (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="text-left text-gray-500 border-b border-gray-200">
          <th className="py-1 pr-2 font-medium" scope="col">
            <span className="sr-only">Select as target</span>
          </th>
          <th className="py-1 pr-2 font-medium" scope="col">
            Name
          </th>
          <th className="py-1 pr-2 font-medium" scope="col">
            GIS ref
          </th>
          <th className="py-1 pr-2 font-medium" scope="col">
            Distance
          </th>
          <th className="py-1 pr-2 font-medium" scope="col">
            Feeder
          </th>
          <th className="py-1 pr-2 font-medium" scope="col">
            kVA
          </th>
        </tr>
      </thead>
      <tbody>
        {candidates.map(c => {
          const label = c.name ?? `candidate ${c.entity_id}`
          return (
            <tr
              key={c.entity_id}
              className={`border-b border-gray-100 ${selectedCandidateId === c.entity_id ? 'bg-blue-50' : ''}`}
            >
              <td className="py-1 pr-2">
                <input
                  type="radio"
                  name={`candidate-${findingId}`}
                  aria-label={`Select ${label} as the target parent`}
                  checked={selectedCandidateId === c.entity_id}
                  onChange={() => onSelectCandidate(c.entity_id)}
                />
              </td>
              <td className="py-1 pr-2 text-gray-800">{label}</td>
              <td className="py-1 pr-2 font-mono text-gray-600">{c.gis_ref ?? '—'}</td>
              <td className="py-1 pr-2 text-gray-600">{formatDistance(c.distance_m)}</td>
              <td className="py-1 pr-2 text-gray-600">{c.feeder ?? '—'}</td>
              <td className="py-1 pr-2 text-gray-600">{c.kva ?? '—'}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function RelationshipEvidence({
  findingId,
  evidence,
  selectedCandidateId,
  onSelectCandidate,
}: {
  findingId: number
  evidence: RelationshipEvidenceResponse
  selectedCandidateId: number | null
  onSelectCandidate: (entityId: number) => void
}) {
  const conductorCount = conductorFeatureCount(evidence.geojson)
  return (
    <div className="space-y-2">
      <CandidateTable
        findingId={findingId}
        candidates={evidence.candidates}
        selectedCandidateId={selectedCandidateId}
        onSelectCandidate={onSelectCandidate}
      />
      {evidence.truncated && (
        <div className="text-xs text-gray-500">
          Showing {conductorCount} of {evidence.total_count} conductors near this asset.
        </div>
      )}
      <MiniMap
        geojson={evidence.geojson}
        selectedCandidateId={selectedCandidateId}
        onSelectCandidate={onSelectCandidate}
      />
    </div>
  )
}

function AttributeChangedEvidence({ evidence }: { evidence: AttributeChangedEvidenceResponse }) {
  return (
    <div className="text-xs text-gray-700 space-y-1">
      <div>
        Platform has <span className="font-mono">{evidence.platform_value ?? '(empty)'}</span>, GIS has{' '}
        <span className="font-mono">{evidence.gis_value ?? '(empty)'}</span>
        {evidence.source_layer ? ` — source layer ${evidence.source_layer}.` : '.'}
      </div>
      {evidence.shadow ? (
        <div className="text-gray-500">
          GIS last observed{' '}
          <span className="font-mono">
            {evidence.shadow.latest_gis_value === null || evidence.shadow.latest_gis_value === undefined
              ? '(empty)'
              : String(evidence.shadow.latest_gis_value)}
          </span>{' '}
          on {formatTimestamp(evidence.shadow.latest_observed_at)}
          {evidence.shadow.latest_import_run_id !== null
            ? ` (import run ${evidence.shadow.latest_import_run_id}).`
            : '.'}
        </div>
      ) : (
        <div className="text-gray-500">No GIS observation history recorded for this field yet.</div>
      )}
      {evidence.ami_activity && (
        <div className="text-gray-500">{evidence.ami_activity.hint ?? 'No AMI activity information available.'}</div>
      )}
    </div>
  )
}

function MissingFromGisEvidence({ evidence }: { evidence: MissingFromGisEvidenceResponse }) {
  return (
    <div className="text-xs text-gray-700 space-y-1">
      <div>
        Last seen in run {evidence.last_seen_run ?? 'unknown'}
        {evidence.last_seen_generation !== null ? ` (generation ${evidence.last_seen_generation})` : ''}
        {evidence.alias_status ? ` — alias status: ${evidence.alias_status}` : ''}.
      </div>
      <div className={evidence.clock.qualifies ? 'text-amber-700' : 'text-gray-500'}>
        {evidence.clock.qualifies ? 'Eligible to confirm retirement.' : 'Not yet eligible to retire.'}{' '}
        {evidence.clock.reason}.
      </div>
    </div>
  )
}

function IdentityConflictEvidence({ evidence }: { evidence: IdentityConflictEvidenceResponse }) {
  const ownerEntity = evidence.owner.entity
  const ownerAlias = evidence.owner.alias
  return (
    <div className="text-xs text-gray-700 space-y-2">
      <div>
        <div className="font-medium text-gray-600 mb-0.5">Current owner</div>
        {ownerEntity ? (
          <div className="space-y-0.5">
            <div>
              {ownerEntity.name ?? `${ownerEntity.table_name} record ${ownerEntity.record_id}`} — GIS id{' '}
              <span className="font-mono">{ownerEntity.gis_id ?? '—'}</span>
              {ownerEntity.source_layer ? `, source layer ${ownerEntity.source_layer}` : ''}
            </div>
            <div className="text-gray-500">
              Source object id: <span className="font-mono">{ownerEntity.source_object_id ?? '—'}</span>
            </div>
            {ownerEntity.lat !== null && ownerEntity.lon !== null && (
              <div className="text-gray-500">
                Location: {ownerEntity.lat.toFixed(5)}, {ownerEntity.lon.toFixed(5)}
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500">The owner entity record no longer exists.</div>
        )}
        {ownerAlias && (
          <div className="text-gray-500">
            Alias <span className="font-mono">{ownerAlias.normalized_global_id}</span> — status{' '}
            {ownerAlias.status}
            {ownerAlias.last_seen_import_run_id !== null
              ? `, last seen run ${ownerAlias.last_seen_import_run_id}`
              : ''}
            .
          </div>
        )}
        {ownerAlias && <div className="text-gray-500">Alias note: {ownerAlias.note ?? '—'}</div>}
      </div>
      <div>
        <div className="font-medium text-gray-600 mb-0.5">Incoming claim</div>
        <div>
          GIS id <span className="font-mono">{evidence.incoming.gis_id ?? '—'}</span> — platform value{' '}
          <span className="font-mono">{evidence.incoming.claimed_tuple.platform_value ?? '(empty)'}</span>, GIS
          value <span className="font-mono">{evidence.incoming.claimed_tuple.gis_value ?? '(empty)'}</span>
        </div>
      </div>
      <div className="text-gray-500 italic">
        No incoming attribute snapshot or location exists for this claim — the refusal that created this finding
        did not retain one. A fuller conflict snapshot is deferred to Phase 2B.
      </div>
    </div>
  )
}

interface EvidencePanelProps {
  findingId: number
  evidence: EvidenceResponse | null
  isLoading: boolean
  error: unknown
  selectedCandidateId: number | null
  onSelectCandidate: (entityId: number) => void
}

export function EvidencePanel({
  findingId,
  evidence,
  isLoading,
  error,
  selectedCandidateId,
  onSelectCandidate,
}: EvidencePanelProps) {
  if (isLoading) {
    return <div className="text-xs text-gray-500">Loading evidence…</div>
  }
  if (error) {
    return (
      <div role="alert" className="text-xs text-red-600">
        {friendlyError(error, 'load evidence for this finding')}
      </div>
    )
  }
  if (!evidence) {
    return null
  }

  switch (evidence.finding_type) {
    case 'relationship_conflict':
    case 'relationship_ambiguous':
    case 'relationship_unresolved':
      return (
        <RelationshipEvidence
          findingId={findingId}
          evidence={evidence}
          selectedCandidateId={selectedCandidateId}
          onSelectCandidate={onSelectCandidate}
        />
      )
    case 'attribute_changed':
    case 'identity_key_change':
      return <AttributeChangedEvidence evidence={evidence} />
    case 'missing_from_gis':
      return <MissingFromGisEvidence evidence={evidence} />
    case 'identity_conflict':
      return <IdentityConflictEvidence evidence={evidence} />
    default:
      return null
  }
}
